import { ConnectedUser } from '../stores/types/connection';
import {
	getProfileFromStreamName,
	isStreamScreenshare,
	parseStreamConnectionData,
} from './utils';

/**
 * Pre-computed sets of feed identifiers needed for the current and next shot.
 * Built once per shot change and passed into {@link createIsStreamNeeded}.
 */
export interface NeededFeeds {
	profiles: Set<number>;
	sipStreamIds: Set<string>;
	onsiteLocationIds: Set<number>;
	screenshareEmails: Set<string>;
}

/**
 * Returns a predicate that checks whether a given OpenTok stream is needed for
 * the current or next shot.  Used by both the embed subscribe-gate in
 * opentok-context and the resolution/pause logic in use-resolution-update so
 * the two stay in sync.
 */
export function createIsStreamNeeded(
	needed: NeededFeeds,
	connectedUsers: ConnectedUser[],
): (stream: { streamId: string; name?: string }) => boolean {
	return (stream) => {
		const streamName = stream.name || '';
		const connData = parseStreamConnectionData(stream);
		const isSip = connData?.sip === true;
		const streamOnsiteId: number | undefined = connData?.onsiteLocationId;

		if (isSip) {
			return needed.sipStreamIds.has(stream.streamId);
		}
		if (streamOnsiteId != null && streamOnsiteId !== 0) {
			return needed.onsiteLocationIds.has(streamOnsiteId);
		}
		if (isStreamScreenshare(stream)) {
			const profile = getProfileFromStreamName(streamName);
			const user = connectedUsers.find(
				(u) => u.gr_user === profile || u.brandlive_profile === profile,
			);
			return !!user && needed.screenshareEmails.has(user.email);
		}

		const profile = getProfileFromStreamName(streamName);
		// Unparseable profiles (NaN, 0, negative) are treated as needed so an
		// unrecognised stream is never incorrectly dropped.
		if (Number.isNaN(profile) || profile <= 0) return true;
		return needed.profiles.has(profile);
	};
}
